import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { GenericAction } from '@/utility/reducers';

export type DestinationType = 'caliza_wallet' | 'external';

export interface EarningsClaimFlowState {
  readonly destinationType: DestinationType;
  readonly fromAmount: number | null;
  readonly fromCurrency: CurrencyCodeType;
  readonly targetCurrency: CurrencyCodeType;
  readonly paymentRail: PaymentRailType;
  readonly recipientId: string | null;
  readonly onlyMoveToCustody: boolean;
}

export const initialState: EarningsClaimFlowState = {
  destinationType: 'caliza_wallet',
  fromAmount: null,
  fromCurrency: CurrencyCodeType.USDC,
  targetCurrency: CurrencyCodeType.USDC,
  paymentRail: PaymentRailType.CalizaWallet,
  recipientId: '',
  onlyMoveToCustody: true,
};

export enum EarningsClaimActionType {
  destinationTypeChanged = 'destinationTypeChanged',
  recipientChanged = 'recipientChanged',
  amountChanged = 'amountChanged',
  targetCurrencyChanged = 'targetCurrencyChanged',
  reset = 'reset',
}

export type DestinationTypeChangedAction = GenericAction<
  EarningsClaimActionType.destinationTypeChanged,
  DestinationType
>;

export type RecipientChangedAction = GenericAction<
  EarningsClaimActionType.recipientChanged,
  { recipientId: string; paymentRail: PaymentRailType; currency: CurrencyCodeType }
>;

export type AmountChangedAction = GenericAction<
  EarningsClaimActionType.amountChanged,
  number | null
>;

export type TargetCurrencyChangedAction = GenericAction<
  EarningsClaimActionType.targetCurrencyChanged,
  CurrencyCodeType
>;

export type ResetAction = GenericAction<EarningsClaimActionType.reset, void>;

export type EarningsClaimAction =
  | DestinationTypeChangedAction
  | RecipientChangedAction
  | AmountChangedAction
  | TargetCurrencyChangedAction
  | ResetAction;

export const earningsClaimReducer = (
  state: EarningsClaimFlowState,
  action: EarningsClaimAction,
): EarningsClaimFlowState => {
  switch (action.type) {
    case EarningsClaimActionType.destinationTypeChanged: {
      const destinationType = action.payload;
      if (destinationType === 'caliza_wallet') {
        return {
          ...state,
          destinationType,
          onlyMoveToCustody: true,
          targetCurrency: CurrencyCodeType.USDC,
          paymentRail: PaymentRailType.CalizaWallet,
          recipientId: null,
        };
      }
      return {
        ...state,
        destinationType,
        onlyMoveToCustody: false,
        targetCurrency: CurrencyCodeType.UNSET,
        paymentRail: PaymentRailType.unset,
        recipientId: null,
      };
    }
    case EarningsClaimActionType.recipientChanged: {
      return {
        ...state,
        recipientId: action.payload.recipientId,
        paymentRail: action.payload.paymentRail,
        targetCurrency: action.payload.currency,
      };
    }
    case EarningsClaimActionType.amountChanged: {
      return {
        ...state,
        fromAmount: action.payload,
      };
    }
    case EarningsClaimActionType.targetCurrencyChanged: {
      return {
        ...state,
        targetCurrency: action.payload,
      };
    }
    case EarningsClaimActionType.reset: {
      return initialState;
    }
    default:
      return state;
  }
};

export const destinationTypeChanged = (payload: DestinationType): DestinationTypeChangedAction => ({
  type: EarningsClaimActionType.destinationTypeChanged,
  payload,
});

export const recipientChanged = (
  payload: RecipientChangedAction['payload'],
): RecipientChangedAction => ({
  type: EarningsClaimActionType.recipientChanged,
  payload,
});

export const amountChanged = (payload: number | null): AmountChangedAction => ({
  type: EarningsClaimActionType.amountChanged,
  payload,
});

export const targetCurrencyChanged = (payload: CurrencyCodeType): TargetCurrencyChangedAction => ({
  type: EarningsClaimActionType.targetCurrencyChanged,
  payload,
});
